import debounce from "lodash/debounce";

import { resolveCommentCountQueryEntry } from "@framework/comments/comment-count-engagement-token";
import type {
  CommentCountsApiResponse,
  CommentCountsResponse,
} from "@framework/comments/comments.types";
import {
  commentCountStore,
  type CommentCountUpdate,
} from "@framework/coral/CommentCountStore";
import { CoralIndexedDBUtils } from "@framework/coral/coral-indexeddb";

const CYCLONE_COUNTS_API_PATH = "/api/cyclone-comment-counts";

interface PendingCountRequest {
  countKey: string;
  queryParam: string;
}

/**
 * Comment count client for Cyclone communities (Honeypot bulk API).
 * Fanposts always use CY: tokens; WordPress articles use WP: tokens.
 */
class CycloneCommentsService {
  private pendingRequests: PendingCountRequest[] = [];
  private registeredCountKeys = new Set<string>();
  private debouncedFetch: () => void;

  constructor() {
    this.debouncedFetch = debounce(
      () => {
        void this.fetchCommentCounts();
      },
      100,
      { maxWait: 1000 },
    );
  }

  /**
   * Register a count key (fanpost shortID → CY:, article/global id → WP:).
   */
  registerCountId(countKey: string): void {
    const entry = resolveCommentCountQueryEntry(countKey);
    if (!entry) {
      return;
    }
    this.registerCountKey(entry.countKey, entry.token);
  }

  private registerCountKey(countKey: string, queryParam: string): void {
    if (this.registeredCountKeys.has(countKey)) {
      return;
    }

    this.registeredCountKeys.add(countKey);
    this.pendingRequests.push({ countKey, queryParam });
    this.debouncedFetch();
  }

  private async fetchCommentCounts(): Promise<void> {
    const requests = [...this.pendingRequests];
    this.pendingRequests = [];

    if (requests.length === 0) {
      return;
    }

    try {
      const params = new URLSearchParams();
      requests
        .sort((a, b) => a.queryParam.localeCompare(b.queryParam))
        .forEach((request) => params.append("c", request.queryParam));

      const response = await fetch(
        `${CYCLONE_COUNTS_API_PATH}?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const responseData = (await response.json()) as CommentCountsApiResponse;
      const counts: CommentCountsResponse = responseData.data;

      const lastModifiedHeader = response.headers.get("Last-Modified");
      const dataTimestamp = lastModifiedHeader
        ? new Date(lastModifiedHeader).getTime()
        : Date.now();

      const updates: CommentCountUpdate[] = [];

      for (const request of requests) {
        const value = counts[request.countKey];
        if (typeof value !== "number") {
          continue;
        }

        updates.push({
          countKey: request.countKey,
          count: value,
          timestamp: dataTimestamp,
        });
      }

      if (updates.length > 0) {
        await commentCountStore.updateCounts(updates);
      }
    } catch (error) {
      console.error("Failed to fetch cyclone comment counts:", error);
    }
  }

  async updateCommentCount(
    countKey: string,
    count: number,
    timestamp: number,
  ): Promise<void> {
    await commentCountStore.updateCommentCount({
      countKey,
      count,
      timestamp,
    });
  }

  getCommentCount(countKey: string): number | null {
    return commentCountStore.getCommentCount(countKey);
  }

  subscribeToCommentCount(
    countKey: string,
    callback: (count: number) => void,
  ): () => void {
    return commentCountStore.subscribe(countKey, callback);
  }

  async loadAllCountsFromIndexedDB(): Promise<void> {
    try {
      const db = await CoralIndexedDBUtils.init();
      const updates = await db.loadAllCommentCounts();

      if (updates.length > 0) {
        await commentCountStore.updateCounts(updates);
      }
    } catch (error) {
      console.warn("IndexedDB not available, skipping count loading:", error);
    }
  }

  async getLastReadCount(countKey: string): Promise<number | null> {
    try {
      const db = await CoralIndexedDBUtils.init();
      return await db.getLastReadCount(countKey);
    } catch (error) {
      console.warn("IndexedDB failed, falling back to localStorage:", error);
      return this.getLastReadCountFromLocalStorage(countKey);
    }
  }

  private getLastReadCountFromLocalStorage(countKey: string): number | null {
    const stored = localStorage.getItem(`comment-count-${countKey}`);
    if (stored) {
      return parseInt(stored, 10);
    }
    return null;
  }

  async setLastReadCount(countKey: string, count: number): Promise<void> {
    try {
      const db = await CoralIndexedDBUtils.init();
      await db.setLastReadCount(countKey, count);
    } catch (error) {
      console.warn("IndexedDB failed, falling back to localStorage:", error);
      localStorage.setItem(`comment-count-${countKey}`, count.toString());
    }
  }
}

let cycloneCommentsServiceInstance: CycloneCommentsService | null = null;

function getCycloneCommentsService(): CycloneCommentsService {
  if (!cycloneCommentsServiceInstance) {
    cycloneCommentsServiceInstance = new CycloneCommentsService();
  }
  return cycloneCommentsServiceInstance;
}

/** Resets the singleton between tests. */
export function resetCycloneCommentsServiceForTests(): void {
  cycloneCommentsServiceInstance = null;
}

export const cycloneCommentsService = {
  getCommentCount: (countKey: string) =>
    getCycloneCommentsService().getCommentCount(countKey),
  updateCommentCount: (countKey: string, count: number, timestamp: number) =>
    getCycloneCommentsService().updateCommentCount(countKey, count, timestamp),
  subscribeToCommentCount: (
    countKey: string,
    callback: (count: number) => void,
  ) => getCycloneCommentsService().subscribeToCommentCount(countKey, callback),
  registerCountId: (countKey: string) =>
    getCycloneCommentsService().registerCountId(countKey),
  loadAllCountsFromIndexedDB: () =>
    getCycloneCommentsService().loadAllCountsFromIndexedDB(),
  getLastReadCount: (countKey: string) =>
    getCycloneCommentsService().getLastReadCount(countKey),
  setLastReadCount: (countKey: string, count: number) =>
    getCycloneCommentsService().setLastReadCount(countKey, count),
};
